import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Mail, Lock, Eye, EyeOff, Briefcase, ArrowRight,
  User, ChevronLeft, Globe, Loader2, CheckCircle2
} from 'lucide-react';
import './CandidateLogin.css';

const CandidateLogin = () => {
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/candidate/login`,
        { username: email, password }
      );
      const { isFormFilled, userId } = res.data;
      localStorage.setItem('userId', userId);
      if (!isFormFilled) navigate('/candidate-details-form');
      else navigate('/candidate-dashboard');
    } catch (error) {
      alert(error.response?.data?.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/candidate/register`,
        { username, email, password }
      );
      alert(res.data.message);
      setView('login');
    } catch (error) {
      alert(error.response?.data?.message || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert("New password and confirm password do not match!");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/candidate/change-password`,
        { email, newPassword }
      );
      alert('Password changed successfully!');
      setView('login');
    } catch (error) {
      alert('Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const getMeta = () => {
    if (view === 'login') return { title: 'Welcome Back', sub: 'Sign in to access your candidate profile' };
    if (view === 'register') return { title: 'Create Profile', sub: 'Join the global hospitality network' };
    return { title: 'Reset Password', sub: 'Enter details to restore access' };
  };
  const meta = getMeta();

  return (
    <div className="cand-auth">
      <div
        className="cand-bg-image"
        style={{ transform: `translate3d(${mousePos.x * -10}px, ${mousePos.y * -6}px, 0) scale(1.05)` }}
      />
      <div className="cand-bg-overlay" />
      <div className="cand-bg-vignette" />
      <div
        className="cand-bg-mesh cand-mesh-1"
        style={{ transform: `translate3d(${mousePos.x * 20}px, ${mousePos.y * 20}px, 0)` }}
      />
      <div
        className="cand-bg-mesh cand-mesh-2"
        style={{ transform: `translate3d(${mousePos.x * -18}px, ${mousePos.y * -18}px, 0)` }}
      />

      <div className="cand-top-bar">
        <div className="cand-status">
          <span className="cand-pulse-dot" />
          <span>SECURE SESSION</span>
        </div>
        <div className="cand-top-meta">
          <span className="cand-time">{formatTime(currentTime)}</span>
          <span className="cand-dot">·</span>
          <span>{formatDate(currentTime)}</span>
          <span className="cand-dot">·</span>
          <span className="cand-region"><Globe size={11} /> DXB</span>
        </div>
      </div>

      <div className="cand-content">
        <div className="cand-brand-col">
          <div className="cand-brand-mark">
            <div className="cand-brand-icon">
              <Briefcase size={20} strokeWidth={2} />
            </div>
            <div className="cand-brand-text">
              <span className="cand-brand-name">HOTELPLACEMENT</span>
              <span className="cand-brand-sub">Candidate Network</span>
            </div>
          </div>

          <h1 className="cand-headline">
            Your Gateway to <br />
            <span className="cand-gold-text">Career Excellence</span>
          </h1>

          <p className="cand-subtext">
            Build a distinguished professional profile and connect with
            premium hospitality opportunities across the globe.
          </p>

          <div className="cand-foot-meta">
            <div className="cand-foot-item">
              <CheckCircle2 size={13} />
              <span>Verified opportunities</span>
            </div>
            <div className="cand-foot-item">
              <Lock size={13} />
              <span>End-to-end encryption</span>
            </div>
          </div>
        </div>

        <div className="cand-form-col">
          <div className="cand-panel">
            <div className="cand-panel-accent" />
            <div className="cand-panel-corner cand-c-tl" />
            <div className="cand-panel-corner cand-c-tr" />
            <div className="cand-panel-corner cand-c-bl" />
            <div className="cand-panel-corner cand-c-br" />

            <div className="cand-security-badge">
              <Lock size={10} />
              <span>TLS 1.3 SECURE</span>
            </div>

            <div className="cand-panel-header">
              <h2>{meta.title}</h2>
              <p>{meta.sub}</p>
            </div>

            {view === 'login' && (
              <form onSubmit={handleLogin} className="cand-form">
                <div className="cand-field">
                  <label>Email Address</label>
                  <div className="cand-input-wrap">
                    <Mail className="cand-input-icon" size={16} />
                    <input type="email" placeholder="name@example.com"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="cand-field">
                  <label>Password</label>
                  <div className="cand-input-wrap">
                    <Lock className="cand-input-icon" size={16} />
                    <input type={showPassword ? "text" : "password"} placeholder="••••••••••••"
                      value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="button" className="cand-eye-btn"
                      onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="cand-form-extras">
                  <button type="button" className="cand-forgot-link"
                    onClick={() => setView('forgot')}>
                    Forgot Password?
                  </button>
                </div>

                <button type="submit" className="cand-submit" disabled={isLoading}>
                  <span className="cand-submit-bg" />
                  <span className="cand-submit-content">
                    {isLoading ? (
                      <><Loader2 className="cand-spin" size={17} /><span>Signing in...</span></>
                    ) : (
                      <><span>Secure Login</span><ArrowRight size={15} className="cand-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <div className="cand-toggle-row">
                  <span>New candidate?</span>
                  <button type="button" className="cand-toggle-link" onClick={() => setView('register')}>
                    Create account
                  </button>
                </div>
              </form>
            )}

            {view === 'register' && (
              <form onSubmit={handleRegister} className="cand-form">
                <div className="cand-field cand-slide">
                  <label>Full Name</label>
                  <div className="cand-input-wrap">
                    <User className="cand-input-icon" size={16} />
                    <input type="text" placeholder="Your full name"
                      value={username} onChange={(e) => setUsername(e.target.value)} required />
                  </div>
                </div>

                <div className="cand-field cand-slide" style={{ animationDelay: '60ms' }}>
                  <label>Email Address</label>
                  <div className="cand-input-wrap">
                    <Mail className="cand-input-icon" size={16} />
                    <input type="email" placeholder="you@example.com"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="cand-field cand-slide" style={{ animationDelay: '120ms' }}>
                  <label>Password</label>
                  <div className="cand-input-wrap">
                    <Lock className="cand-input-icon" size={16} />
                    <input type={showPassword ? "text" : "password"} placeholder="Create strong password"
                      value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="button" className="cand-eye-btn"
                      onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="cand-submit" disabled={isLoading}>
                  <span className="cand-submit-bg" />
                  <span className="cand-submit-content">
                    {isLoading ? (
                      <><Loader2 className="cand-spin" size={17} /><span>Creating...</span></>
                    ) : (
                      <><span>Register Now</span><ArrowRight size={15} className="cand-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <button type="button" className="cand-back-link" onClick={() => setView('login')}>
                  <ChevronLeft size={14} /> <span>Have an account? <strong>Sign in</strong></span>
                </button>
              </form>
            )}

            {view === 'forgot' && (
              <form onSubmit={handleForgotPasswordSubmit} className="cand-form">
                <div className="cand-field cand-slide">
                  <label>Email Address</label>
                  <div className="cand-input-wrap">
                    <Mail className="cand-input-icon" size={16} />
                    <input type="email" placeholder="Registered email"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="cand-field cand-slide" style={{ animationDelay: '60ms' }}>
                  <label>New Password</label>
                  <div className="cand-input-wrap">
                    <Lock className="cand-input-icon" size={16} />
                    <input type="password" placeholder="New password"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                  </div>
                </div>

                <div className="cand-field cand-slide" style={{ animationDelay: '120ms' }}>
                  <label>Confirm Password</label>
                  <div className="cand-input-wrap">
                    <Lock className="cand-input-icon" size={16} />
                    <input type="password" placeholder="Confirm password"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                  </div>
                </div>

                <button type="submit" className="cand-submit" disabled={isLoading}>
                  <span className="cand-submit-bg" />
                  <span className="cand-submit-content">
                    {isLoading ? (
                      <><Loader2 className="cand-spin" size={17} /><span>Updating...</span></>
                    ) : (
                      <><span>Update Password</span><ArrowRight size={15} className="cand-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <button type="button" className="cand-back-link" onClick={() => setView('login')}>
                  <ChevronLeft size={14} /> <span>Back to <strong>Login</strong></span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateLogin;